import { useRef } from 'react';
import { PiChatCircle } from 'react-icons/pi';
import type { FunnelConversationDocument } from 'src/api/generated';
import type { ConversationListProps } from 'src/modules/communications/types/conversation-list.type';
import Loading from 'src/modules/shared/components/atoms/loading';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { useInfiniteScroll } from 'src/modules/shared/hooks/scroll/movement';
import { ConversationCardRenderer } from '../conversations/conversation-card-renderer';
import { ConversationCardSkeletonRenderer } from '../conversations/conversation-card-skeleton-renderer';

const ConversationList: React.FC<ConversationListProps> = ({
  isLoading,
  error,
  conversations = [],
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  onConversationSelect,
  onOpenContactProfile,
  selectedConversationId,
  onRetry,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useInfiniteScroll({
    containerRef,
    isFetching: Boolean(isFetchingNextPage),
    hasMore: Boolean(hasNextPage),
    fetchMore: () => onLoadMore?.(),
    direction: 'down',
    threshold: 0.3,
  });

  const handleConversationClick = (
    conversation: FunnelConversationDocument
  ) => {
    onConversationSelect?.(Number(conversation.id));
  };

  if (error) {
    return (
      <ErrorState
        // TODO: check when implementing i18n
        title="Error al cargar conversaciones"
        // TODO: check when implementing i18n
        description="No se pudo cargar las conversaciones. Por favor, inténtalo de nuevo."
        onAction={onRetry}
        variant="primary"
      />
    );
  }

  return (
    <div className="flex flex-col h-full w-full">
      <div
        ref={containerRef}
        data-testid="conversation-list-scroll"
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
      >
        {isLoading ? (
          <div className="mx-auto flex w-full max-w-[1080px] flex-col gap-1.5 px-1.5 py-1.5 sm:px-2 sm:py-2 md:px-3">
            {Array.from({ length: 5 }).map((_, index) => (
              <ConversationCardSkeletonRenderer key={index} />
            ))}
          </div>
        ) : conversations.length === 0 ? (
          // TODO: check when implementing i18n
          <EmptyState
            title="No hay conversaciones"
            description="Inicia una nueva conversación o cambia tus filtros"
            icon={<PiChatCircle className="text-primary-500 w-full h-full" />}
          />
        ) : (
          <div className="mx-auto flex w-full max-w-[1080px] flex-col gap-1.5 px-1.5 py-1.5 sm:px-2 sm:py-2 md:px-3">
            {conversations.map((conversation) => (
              <ConversationCardRenderer
                key={conversation.id}
                conversation={conversation}
                onClick={() => handleConversationClick(conversation)}
                isSelected={conversation.id === selectedConversationId}
                layout="list"
                onOpenDetails={onOpenContactProfile}
              />
            ))}

            {isFetchingNextPage && hasNextPage && (
              <Loading size="sm" variant="neutral" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;
